import { differenceInSeconds } from 'date-fns';
import { notifyError } from '../../../utils/bugsnag/notifyError';
import { clearBugsnagUser } from '../../../utils/bugsnag/userContext';
import BaseAuthentication from './BaseAuthentication';
import { AuthLogoutReasons } from '../Constants';

const { FORCED_LOGOUT, EXPIRED_TOKEN, USER_LOGOUT } = AuthLogoutReasons;

/**
 * @access public
 * @class
 *
 * @param {Object} param - Authentication constructor params.
 * @param {Storage} param.storage - A storage instance to store the auth tokens.
 * @param {OAuthentication~loginRequest} param.loginRequest - A login request callback.
 * @param {OAuthentication~getProfileRequest} param.getProfileRequest - A get profile request callback.
 * @param {OAuthentication~refreshTokenRequest} param.refreshTokenRequest - A refresh token request callback.
 * @param {OAuthentication~deauthorizeRequest} param.deauthorizeRequest - A deauthorize request callback.
 * @param {OAuthentication~navigateToLogin} param.navigateToLogin - A callback to navigate to login screen on logout.
 * @param {string} param.rolesAllowed - Comma separated list of allowed roles. E.g. "Admin,Manager".
 * @example
 * import { OAuthentication } from "@eatzy/common-reactjs/Authentication";
 *
 * const Authentication = new OAuthentication({
 *   storage, // Should be an instance of the Storage lib module
 *   loginRequest: ({email, password}) => yourLoginRequestCallback(email, password),
 *   getProfileRequest:  () => yourGetProfileRequestCallback(),
 *   refreshTokenRequest: (token) => yourRefreshTokenRequestCallback(token),
 *   deauthorizeRequest: (token) => yourLogoutRequestCallback(token),
 *   navigateToLogin: () => yourNavigateToLoginCallback(),
 *   rolesAllowed: "Admin,Owner",
 * });
 *
 */
export default class OAuthentication extends BaseAuthentication {
  storage = null;

  constructor({
    storage,
    loginRequest,
    registerRequest,
    getProfileRequest,
    refreshTokenRequest,
    deauthorizeRequest,
    navigateToLogin,
    rolesAllowed,
    forgotPasswordRequest,
    resetPasswordRequest,
  }) {
    super();
    this.storage = storage;
    this.navigateToLogin = navigateToLogin;
    this.loginRequest = loginRequest;
    this.registerRequest = registerRequest;
    this.getProfileRequest = getProfileRequest;
    this.refreshTokenRequest = refreshTokenRequest;
    this.deauthorizeRequest = deauthorizeRequest;
    this.rolesAllowed = rolesAllowed;
    this.forgotPasswordRequest = forgotPasswordRequest;
    this.resetPasswordRequest = resetPasswordRequest;
  }

  /**
   * Method to register user.
   */
  register = async ({
    username,
    firstname,
    lastname,
    password,
    email,
    source,
    profile,
    phone,
  }) =>
    await this.registerRequest({
      username,
      firstname,
      lastname,
      password,
      email,
      source,
      profile,
      phone,
    });

  /**
   * Method to login user.
   * Calls Auth Hub in two steps:
   *   1. POST /auth/login  → session token (stored as sessionToken)
   *   2. POST /account/authorize → Eatzy access/refresh tokens + roles
   *
   * Stores the combined shape { access, refresh, roles, sessionToken } in storage.
   * The post-login profile fetch is responsible for handling Eatzy API
   * read-replica lag via a bounded retry/backoff in the caller (UserSaga.signIn);
   * no global mutable flag is needed here.
   */
  login = async (email, password) => {
    const authInfo = await this.loginRequest({ email, password });
    await this.storage.set('user', authInfo);
  };

  /**
   * Gets the token from storage, checks if it is expired.
   * If expired, attempts to get a new token by calling the refreshToken method.
   */
  getToken = async (isBackEndTokenExpired = false) => {
    const authInfo = await this.storage.get('user');

    if (!authInfo) return null;
    const accessToken = authInfo?.access?.token;
    const expiresIn = authInfo?.access?.expires * 1000;

    if (
      !isBackEndTokenExpired &&
      !this._isTokenExpired(accessToken, expiresIn)
    ) {
      return { token: accessToken, isExpired: false };
    }
    try {
      const refreshedToken = await this.refreshToken();
      return {
        token: refreshedToken?.access?.token,
        isExpired: this._isTokenExpired(
          refreshedToken?.access?.token,
          refreshedToken?.access?.expires * 1000
        ),
      };
    } catch (err) {
      await this.logout(EXPIRED_TOKEN);
      return { token: accessToken, isExpired: true };
    }
  };

  _hasRolePermissions = async (roles) => {
    const rolesAllowed = JSON.parse(this.rolesAllowed);

    if (!Object.keys(rolesAllowed).length) return true;

    const allowed = Object.entries(rolesAllowed).reduce(
      (acc, [key, value]) => {
        const roleValue = roles[key];
        // Auth Hub may return boolean true (array or boolean role format).
        // Legacy Eatzy API returns a string sub-role (e.g. "Manager").
        // Both are accepted: boolean true means the role is present; a matching
        // string means the role level is in the allowed list.
        return acc || roleValue === true || value.includes(roleValue);
      },
      false
    );

    return allowed;
  };

  /**
   * Gets the currently logged in user profile.
   * Passes the stored session token to getProfileRequest so it can call
   * Auth Hub /auth/me first, falling back to Eatzy API /account/profile.
   * Merges stored roles into the returned profile so role validation works
   * regardless of whether the profile came from Auth Hub or the Eatzy API.
   */
  getProfile = async () => {
    try {
      const authInfo = await this.storage.get('user');
      const sessionToken = authInfo?.sessionToken || null;
      const profile = await this.getProfileRequest(sessionToken);

      const roles = profile?.roles || authInfo?.roles || {};
      const profileWithRoles = { ...profile, roles };

      const hasPermissions = await this._hasRolePermissions(roles);
      if (!hasPermissions) {
        throw new Error('Insufficient permissions');
      }
      return profileWithRoles;
    } catch (err) {
      if (err?.message === 'Insufficient permissions') throw err;
      throw Error('Unable to get profile');
    }
  };

  _isTokenExpired = (token, expiresIn) =>
    !token || differenceInSeconds(new Date(expiresIn), new Date()) <= 0;

  /**
   * Manually request for a token refresh.
   */
  refreshToken = async () => {
    const authInfo = await this.storage.get('user');

    if (authInfo?.refresh) {
      try {
        const refreshToken = await this.refreshTokenRequest(
          authInfo?.refresh?.token
        );
        if (!refreshToken) throw new Error();
        const newToken = {
          ...authInfo,
          access: refreshToken,
        };
        await this.storage.set('user', newToken);
        return newToken;
      } catch (err) {
        throw Error('Unable to refresh token');
      }
    } else throw Error('Unable to refresh token');
  };

  /**
   * Signs out the user, and removes the token from storage.
   */
  logout = async (reason, shouldRedirect = true) => {
    try {
      const authInfo = await this.storage.get('user');
      if (authInfo?.refresh) {
        await this.deauthorizeRequest(authInfo?.refresh?.token);
      }

      await this.storage.remove('user');
      clearBugsnagUser();
      if (shouldRedirect) this.navigateToLogin();
      return this.logoutCallback(reason);
    } catch (err) {
      notifyError(err, { component: 'OAuthentication', method: 'logout' });
      console.error('Error deauthorizing account');
      await this.storage.remove('user');
      clearBugsnagUser();
      if (shouldRedirect) this.navigateToLogin();
      return this.logoutCallback(reason);
    }
  };

  logoutCallback = async (reason) => {
    switch (reason) {
      case EXPIRED_TOKEN:
        return {
          message: 'Session Expired',
          options: {
            key: EXPIRED_TOKEN,
            variant: 'warning',
          },
        };
      case FORCED_LOGOUT:
        return {
          message: 'Session invalidated',
          options: {
            key: FORCED_LOGOUT,
            variant: 'error',
          },
        };
      case USER_LOGOUT:
        return {
          message: 'Success logout',
          options: {
            key: USER_LOGOUT,
            variant: 'success',
          },
        };

      default:
        return null;
    }
  };
}
